import AppModal from '@/components/AppModal/AppModal';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { openErrorToast, openToast } from '@/helpers';
import { useGetLocationsQuery, useUpdateLocationPositionMutation } from '@/redux/query';
import { IListLocation } from '@/types/components/location';
import { Box, Icon } from '@shopify/polaris';
import { DragHandleIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';

interface Props {
  open: boolean;
  onCancel: () => void;
  listLocations: IListLocation[];
}

export const ModalSortLocation = ({ open, onCancel, listLocations }: Props) => {
  const [updateLocationPosition, { isLoading: isLoadingLocationPosition }] = useUpdateLocationPositionMutation();
  const { isLoading: isLocationLoading, refetch: refetchLocationData } = useGetLocationsQuery({ shop: config.shop });
  const dispatch = useDispatch();
  const initialColumns = useMemo(
    () =>
      listLocations.map((location) => ({
        value: location.id,
        label: location.name,
      })),
    [listLocations],
  );

  const [orderColumns, setOrderColumns] = useState(initialColumns);

  useEffect(() => {
    setOrderColumns(initialColumns);
  }, [initialColumns]);

  const handleDragEnd = useCallback(
    (result: any) => {
      const { source, destination } = result;

      if (!destination) return;

      const updated = Array.from(orderColumns);
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);
      setOrderColumns(updated);
    },
    [orderColumns],
  );

  return (
    <AppModal
      open={open}
      onCancel={() => {
        setOrderColumns(initialColumns);
        onCancel();
      }}
      onSubmit={() => {
        updateLocationPosition({
          shop: config.shop,
          locations: orderColumns.map((item, index) => ({
            id: item.value,
            position: index + 1,
          })),
        })
          .unwrap()
          .then(() => {
            openToast(dispatch);
            refetchLocationData();
          })
          .then(() => onCancel())
          .catch(() => {
            openErrorToast(dispatch);
            onCancel();
          });
      }}
      title={'Arrange Locations'}
      submitText={'Submit'}
      cancelText={'Cancel'}
      loading={isLoadingLocationPosition || isLocationLoading}
    >
      <Box>
        <Box paddingBlockEnd={'300'}>
          <BoldText>Customize and Reorder locations</BoldText>
          <RegularText>Drag items to reorder and this order will appear in storefront</RegularText>
        </Box>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="columns" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ flex: 1 }}>
                {orderColumns.map((choice, index) => (
                  <Draggable key={choice.value} draggableId={choice.value.toString()} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: index === orderColumns.length - 1 ? '0' : '8px',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          padding: '8px',
                          background: snapshot.isDragging ? '#f0f0f0' : 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                        }}
                      >
                        <Box>
                          <Icon source={DragHandleIcon} />
                        </Box>
                        <RegularText>{choice.label}</RegularText>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </AppModal>
  );
};
